import Icon from 'components/_UI/Icon';
import Text from 'components/_UI/Text';
import React from 'react';
import styled, { css } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import sizes from 'theme/sizes';
import Background from './Background';
import PropTypes from 'prop-types';
import Button from 'components/_UI/Button';
import { Check } from 'lucide-react';

/**
 * A chamada da esquerda é a promessa da página, e ela mudava de dono: quem
 * abria "quero ser parceiro" lia "Encontre eventos e coisas novas" — o texto
 * de cadastro de consumidor. Quem quer vender no Whizr precisa de outra
 * resposta: o que dá para fazer aqui e quanto tempo leva para começar.
 */
const copyByVariant = {
    createPartner: {
        title: ['Venda seus ingressos', 'sem montar uma operação.'],
        subtitle: 'Publique o evento, receba por Pix e cartão e acompanhe cada lote vendido em um painel só.',
        highlights: [
            'Sem mensalidade: você só paga quando vende',
            'Cadastro em 3 passos, resposta em até 48h úteis',
            'Com ou sem CNPJ',
        ],
    },
};

const defaultCopy = {
    title: ['Encontre eventos e coisas novas,', 'em qualquer lugar da sua região.'],
    subtitle: 'Crie agora sua conta e descubra tudo novo com Whizr.',
    highlights: [],
};

function PresentationBackground({ ComponentToRender, is }) {
    const copy = copyByVariant[is] || defaultCopy;

    return (
        <Container>
            <Background />

            <LogoContainer>
                <Icon name='whizr-inline-logo' color='white' />
            </LogoContainer>

            <TextContainer>
                <Text name='hero' color={colors.neutralLight}>
                    {copy.title[0]}
                </Text>
                <Text name='hero' color={colors.neutralLight} marginTop='-15px'>
                    {copy.title[1]}
                </Text>
                <Text name='subtitle' color={colors.neutralLight} marginTop='5px'>
                    {copy.subtitle}
                </Text>

                {copy.highlights.length > 0 && (
                    <HighlightList>
                        {copy.highlights.map((item) => (
                            <HighlightItem key={item}>
                                <Check size={16} strokeWidth={3} aria-hidden='true' />
                                {item}
                            </HighlightItem>
                        ))}
                    </HighlightList>
                )}
            </TextContainer>

            <SignContainer
                signIn={is === 'signIn' && colors.neutralDark}
                signUp={is === 'signUp' && colors.neutralDark}
                createPartner={is === 'createPartner' && colors.neutralDark}
                recoverAccount={is === 'recoverAccount' && colors.blueVerticalGradient}
            >
                <ComponentToRender />
            </SignContainer>
        </Container>

    )
};

PresentationBackground.propTypes = {
    is: PropTypes.oneOf(['signIn', 'signUp', 'recoverAccount', 'createPartner'])
};

const Container = styled.div`
    width: 100%;
    height: 100vh;
    display: flex;
`;

const TextContainer = styled.div`
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: block;
        padding-top: 20%;
        padding-left: 4%;
        width: 50%;
    };

    @media screen and (min-width: ${breakpoints.xl}) {
        display: block;
        height: 100vh;
        padding: 17% 4%;
    };
`;

const HighlightList = styled.ul`
    list-style: none;
    margin: 28px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const HighlightItem = styled.li`
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${colors.neutralLight};
    font-size: 15px;
    font-weight: 500;
`;

const LogoContainer = styled.div`
    display: none;
    
    @media screen and (min-width: ${breakpoints.lg}) {
        display: block;
        position: absolute;
        top: 0;
        left: 4%;
        width: 200px;
    };
`;

const SignContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    
    ${({ signIn }) => signIn && css`
        background: ${signIn};
    `};
    
    ${({ signUp }) => signUp && css`
        background: ${signUp};
    `};
    ${({ createPartner }) => createPartner && css`
        background: ${createPartner};
    `};

    ${({ recoverAccount }) => recoverAccount && css`
        background-image: ${recoverAccount};
    `};
    
    @media screen and (min-width: ${breakpoints.lg}) { 
        position: absolute;
        right: 0;
        top: 0;
        width: 33.5%;
        border-radius: ${sizes.grand} ${sizes.none} ${sizes.none} ${sizes.grand};
    }
`;

export default PresentationBackground;
